import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GeocodeSiteLocationRequest,
  GeocodeSiteLocationResponse,
  GetCompanySiteDraftResponse,
  GetSiteIdentificationStatusResponse,
  GetSiteLocationsResponse,
  HasUnintegratedSiteLocationChangesResponse,
  PublishSiteLocationsResponse,
  SaveSiteLocationsRequest,
  SaveSiteLocationsResponse,
} from '@upright/common/model/input/companies/geolocation/api-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { clearBannerDismissal } from '@upright/ui/common/generic/utilities/storage';
import { FREEMIUM_DMA_RESULT_BANNER_ID } from '../../shared/with-calculating-results-banner';

/**
 * React-query hooks for the site-location input tool (PLT-5329).
 *
 * The backend routes live under `/api/company/{companyId}/site-locations/{variantId}` —
 * `fetchOrThrow` prepends the `/api` base (see fetcher-core `getApiUrlBase`), so the paths
 * passed here intentionally omit it. Reads need company READ access and writes company EDIT
 * access; publishing also needs the CALCULATE_CLIMATE_RISK privilege.
 */

const SITE_LOCATIONS_QUERY_KEY = 'site-locations';
const SITE_LOCATIONS_HAS_CHANGES_QUERY_KEY = 'site-locations-has-changes';

/** GET the working set (draft, else linked published, else null) for a variant. */
export function useSiteLocations(companyId: string, variantId: string | null) {
  return useQuery<GetSiteLocationsResponse>({
    queryKey: [SITE_LOCATIONS_QUERY_KEY, companyId, variantId],
    queryFn: async () => {
      if (!variantId) throw new Error('Variant ID is required');
      return await fetchOrThrow<GetSiteLocationsResponse>(`/company/${companyId}/site-locations/${variantId}`, 'GET');
    },
    enabled: !!companyId && !!variantId,
  });
}

/**
 * POST a draft save (transactional replace of the company's draft site list). Does not
 * touch the variant link or queue a recompute — that is the publish step. On success we
 * invalidate the working-set query (so the table reflects the saved draft) and the
 * has-changes query (so the Rerun button flips to enabled).
 */
export function useSaveSiteLocations({ companyId, variantId }: { companyId: string; variantId: string | null }) {
  const queryClient = useQueryClient();
  return useMutation<SaveSiteLocationsResponse, Error, SaveSiteLocationsRequest>({
    mutationKey: ['saveSiteLocations', companyId, variantId],
    mutationFn: async (request: SaveSiteLocationsRequest) => {
      if (!variantId) throw new Error('Variant ID is required');
      return await fetchOrThrow<SaveSiteLocationsResponse>(
        `/company/${companyId}/site-locations/${variantId}`,
        'POST',
        request,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SITE_LOCATIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SITE_LOCATIONS_HAS_CHANGES_QUERY_KEY] });
    },
  });
}

/**
 * POST publish: promotes the draft to published, links the variant (and its tailoring
 * counterpart) to it, and queues a recompute. Same invalidations as save so the button
 * state and working set re-resolve once the draft has been integrated.
 */
export function usePublishSiteLocations({ companyId, variantId }: { companyId: string; variantId: string | null }) {
  const queryClient = useQueryClient();
  return useMutation<PublishSiteLocationsResponse, Error>({
    mutationKey: ['publishSiteLocations', companyId, variantId],
    mutationFn: async () => {
      if (!variantId) throw new Error('Variant ID is required');
      return await fetchOrThrow<PublishSiteLocationsResponse>(
        `/company/${companyId}/site-locations/${variantId}/publish`,
        'POST',
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SITE_LOCATIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SITE_LOCATIONS_HAS_CHANGES_QUERY_KEY] });
      // The "Your results have been updated" success toast tracks dismissal under a persistent
      // localStorage key and re-saves that dismissal when it auto-dismisses (20s) — so it shows
      // only once per browser unless the dismissal is cleared. Clear it on each publish so the
      // toast fires again when this recompute completes (mirrors use-publish-product-set).
      clearBannerDismissal(FREEMIUM_DMA_RESULT_BANNER_ID);
    },
  });
}

/**
 * POST a single address (its parts) to the stateless geocode route and get back coordinates
 * (or `dataAvailable: false` when Google resolved nothing). Used both by the per-row
 * "Identify from address" action and the publish-time "missing coordinates" prompt
 * (PLT-5329 #8). Nothing is persisted server-side — the caller writes the returned
 * coordinates into the editable rows / draft. No query invalidation: this neither reads nor
 * mutates the working set.
 */
export function useGeocodeSiteLocation({ companyId, variantId }: { companyId: string; variantId: string | null }) {
  return useMutation<GeocodeSiteLocationResponse, Error, GeocodeSiteLocationRequest>({
    mutationKey: ['geocodeSiteLocation', companyId, variantId],
    mutationFn: async (request: GeocodeSiteLocationRequest) => {
      if (!variantId) throw new Error('Variant ID is required');
      return await fetchOrThrow<GeocodeSiteLocationResponse>(
        `/company/${companyId}/site-locations/${variantId}/geocode`,
        'POST',
        request,
      );
    },
  });
}

/**
 * PLT-6998: company-scoped variants of the draft hooks above, for custom-company creation, which
 * reviews the sites before the first publish creates any variant. They read and write the same
 * company draft as the variant-scoped hooks (the draft was always keyed by company), so a save
 * here invalidates both families.
 */
export function useCompanySiteDraft(companyId: string | undefined) {
  return useQuery<GetCompanySiteDraftResponse>({
    queryKey: [SITE_LOCATIONS_QUERY_KEY, companyId, 'company-draft'],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID is required');
      return await fetchOrThrow<GetCompanySiteDraftResponse>(`/company/${companyId}/site-locations`, 'GET');
    },
    enabled: !!companyId,
  });
}

export function useSaveCompanySiteDraft(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation<SaveSiteLocationsResponse, Error, SaveSiteLocationsRequest>({
    mutationKey: ['saveCompanySiteDraft', companyId],
    mutationFn: async (request: SaveSiteLocationsRequest) =>
      await fetchOrThrow<SaveSiteLocationsResponse>(`/company/${companyId}/site-locations`, 'POST', request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SITE_LOCATIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SITE_LOCATIONS_HAS_CHANGES_QUERY_KEY] });
    },
  });
}

export function useGeocodeCompanySite(companyId: string) {
  return useMutation<GeocodeSiteLocationResponse, Error, GeocodeSiteLocationRequest>({
    mutationKey: ['geocodeCompanySite', companyId],
    mutationFn: async (request: GeocodeSiteLocationRequest) =>
      await fetchOrThrow<GeocodeSiteLocationResponse>(`/company/${companyId}/site-locations/geocode`, 'POST', request),
  });
}

const SITE_IDENTIFICATION_QUERY_KEY = 'site-identification';

/** How often to re-ask while identification runs; matches the wizard's job-status polling. */
const SITE_IDENTIFICATION_POLL_MS = 3000;

/**
 * PLT-6998: where the company's creation-time site identification stands. Polls while it runs and
 * stops once it settles.
 *
 * A request that has never succeeded answers `none`, the same as "never started", so the caller
 * skips the sites step rather than waiting on a status it cannot read. That also covers a frontend
 * served against a backend without the route, as a preview build is. Once an answer has arrived,
 * a failed refetch keeps it: a poll that fails during a deploy must not flip a running
 * identification to `none`, which would open product review without its sites step for a moment.
 */
export function useSiteIdentificationStatus(companyId: string | undefined) {
  const query = useQuery<GetSiteIdentificationStatusResponse>({
    queryKey: [SITE_IDENTIFICATION_QUERY_KEY, companyId],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID is required');
      return await fetchOrThrow<GetSiteIdentificationStatusResponse>(`/company/${companyId}/site-identification`);
    },
    enabled: !!companyId,
    refetchInterval: current => (current.state.data?.status === 'running' ? SITE_IDENTIFICATION_POLL_MS : false),
    retry: 1,
  });
  const status: GetSiteIdentificationStatusResponse | undefined =
    query.data ?? (query.isError ? { status: 'none' } : undefined);
  return { status };
}

/** GET whether the company has an unpublished site-location draft (drives the Rerun button). */
export function useSiteLocationsHasChanges(companyId: string, variantId: string | null) {
  return useQuery<HasUnintegratedSiteLocationChangesResponse>({
    queryKey: [SITE_LOCATIONS_HAS_CHANGES_QUERY_KEY, companyId, variantId],
    queryFn: async () => {
      if (!variantId) throw new Error('Variant ID is required');
      return await fetchOrThrow<HasUnintegratedSiteLocationChangesResponse>(
        `/company/${companyId}/site-locations/${variantId}/has-changes`,
        'GET',
      );
    },
    enabled: !!companyId && !!variantId,
  });
}
